/**
 * THE SHAPE OF A DANCER'S BAG GROUP VIEW (packet BG-10), read the same way
 * whichever door brought it: the dancer portal for her cookie session, or
 * netlify/functions/bagGroupView.mjs for her Jr Staff email account or her
 * promoted dancer login. Both doors send what
 * netlify/functions/lib/bagGroupView.mjs bagGroupViewForStudent builds, and
 * this file is the one browser reader of it, so the page can never read the
 * two doors differently.
 *
 * Pure on purpose: no Firebase import, so lib/dancerSession.ts and the tests
 * can import it directly. Every field is re-checked; anything malformed is
 * dropped rather than trusted.
 */

export type BagGroupViewItemStatus = 'needed' | 'claimed' | 'dropped-off' | 'done';

export interface BagGroupViewAttachment {
  id: string;
  kind: 'image' | 'video' | 'file';
  name: string;
  contentType: string;
  url: string;
}

export interface BagGroupViewMessage {
  id: string;
  authorName: string;
  authorRole: 'director' | 'staff' | 'family';
  text: string;
  /** ISO time, or null when the server had none it could read. */
  createdAt: string | null;
  attachments: BagGroupViewAttachment[];
}

export interface BagGroupViewPost extends BagGroupViewMessage {
  reactionCounts: Record<string, number>;
  ackCount: number;
  replyCount: number;
  replies: BagGroupViewMessage[];
}

export interface BagGroupViewItem {
  id: string;
  title: string;
  note: string;
  competitionName: string;
  competitionDate: string | null;
  dropOffDate: string | null;
  dropOffLocation: string;
  quantity: string;
  status: BagGroupViewItemStatus;
  ownerName: string;
}

export interface BagGroupViewGroup {
  id: string;
  name: string;
  seasonName: string;
  dancerName: string;
  leadNames: string[];
  items: BagGroupViewItem[];
  /** True when her family has more items than the view carries. */
  itemsTruncated: boolean;
  posts: BagGroupViewPost[];
}

export interface BagGroupView {
  groups: BagGroupViewGroup[];
  /** True when she is in more groups than the view carries. */
  capped: boolean;
  /** True when the lookup itself hit its limit and may have missed a group. */
  scanTruncated: boolean;
}

export const EMPTY_BAG_GROUP_VIEW: BagGroupView = { groups: [], capped: false, scanTruncated: false };

function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function str(value: unknown, max = 4000): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function iso(value: unknown): string | null {
  return typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : null;
}

function dateKey(value: unknown): string | null {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : null;
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
}

function attachment(value: unknown): BagGroupViewAttachment | null {
  const raw = rec(value);
  const url = str(raw.url, 2048);
  const id = str(raw.id, 100);
  if (!id || !/^https:\/\/firebasestorage\.googleapis\.com\//.test(url)) return null;
  const kind = raw.kind === 'image' || raw.kind === 'video' ? raw.kind : 'file';
  return { id, kind, name: str(raw.name, 160) || 'Attachment', contentType: str(raw.contentType, 160), url };
}

function message(value: unknown): BagGroupViewMessage | null {
  const raw = rec(value);
  const id = str(raw.id, 200);
  if (!id) return null;
  return {
    id,
    authorName: str(raw.authorName, 100) || 'Someone in the group',
    authorRole: raw.authorRole === 'director' || raw.authorRole === 'staff' ? raw.authorRole : 'family',
    text: str(raw.text),
    createdAt: iso(raw.createdAt),
    attachments: arr(raw.attachments).map(attachment).filter((item): item is BagGroupViewAttachment => item !== null),
  };
}

function post(value: unknown): BagGroupViewPost | null {
  const base = message(value);
  if (!base) return null;
  const raw = rec(value);
  const reactionCounts: Record<string, number> = {};
  for (const [emoji, n] of Object.entries(rec(raw.reactionCounts))) {
    if (count(n) > 0 && emoji.length <= 16) reactionCounts[emoji] = count(n);
  }
  const replies = arr(raw.replies).map(message).filter((item): item is BagGroupViewMessage => item !== null);
  return {
    ...base,
    reactionCounts,
    ackCount: count(raw.ackCount),
    replyCount: Math.max(count(raw.replyCount), replies.length),
    replies,
  };
}

const ITEM_STATUSES: readonly BagGroupViewItemStatus[] = ['needed', 'claimed', 'dropped-off', 'done'];

function item(value: unknown): BagGroupViewItem | null {
  const raw = rec(value);
  const id = str(raw.id, 200);
  if (!id) return null;
  return {
    id,
    title: str(raw.title, 160) || 'Item',
    note: str(raw.note, 1000),
    competitionName: str(raw.competitionName, 160),
    competitionDate: dateKey(raw.competitionDate),
    dropOffDate: dateKey(raw.dropOffDate),
    dropOffLocation: str(raw.dropOffLocation, 160),
    quantity: str(raw.quantity, 40),
    status: ITEM_STATUSES.includes(raw.status as BagGroupViewItemStatus) ? raw.status as BagGroupViewItemStatus : 'needed',
    ownerName: str(raw.ownerName, 100) || 'Your family',
  };
}

function group(value: unknown): BagGroupViewGroup | null {
  const raw = rec(value);
  const id = str(raw.id, 200);
  if (!id) return null;
  return {
    id,
    name: str(raw.name, 120) || 'Bag group',
    seasonName: str(raw.seasonName, 80),
    dancerName: str(raw.dancerName, 120),
    leadNames: arr(raw.leadNames).map((name) => str(name, 100)).filter(Boolean),
    items: arr(raw.items).map(item).filter((row): row is BagGroupViewItem => row !== null),
    itemsTruncated: raw.itemsTruncated === true,
    posts: arr(raw.posts).map(post).filter((row): row is BagGroupViewPost => row !== null),
  };
}

/** Read whatever a door sent; never throws. */
export function parseBagGroupView(value: unknown): BagGroupView {
  const raw = rec(value);
  return {
    groups: arr(raw.groups).map(group).filter((row): row is BagGroupViewGroup => row !== null),
    capped: raw.capped === true,
    scanTruncated: raw.scanTruncated === true,
  };
}

/** Milliseconds for a view time, or null. Guarded, never throws. */
export function bagViewMillis(value: string | null): number | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/** Every post across her groups, newest first, each with its group. */
export function bagViewRecentPosts(
  view: BagGroupView,
  sinceMs: number | null = null
): Array<{ group: BagGroupViewGroup; post: BagGroupViewPost }> {
  const rows = view.groups.flatMap((g) => g.posts.map((p) => ({ group: g, post: p })));
  return rows
    .filter(({ post: p }) => sinceMs === null || (bagViewMillis(p.createdAt) ?? 0) >= sinceMs)
    .sort((a, b) => (bagViewMillis(b.post.createdAt) ?? 0) - (bagViewMillis(a.post.createdAt) ?? 0)
      || a.post.id.localeCompare(b.post.id));
}

/** The words an item's status reads as, matched to the group page. */
export function bagViewItemStatusLabel(status: BagGroupViewItemStatus): string {
  if (status === 'dropped-off') return 'Dropped off, waiting to be checked in';
  if (status === 'done') return 'Received';
  if (status === 'claimed') return 'Signed up';
  return 'Not assigned';
}
